import { useMemo } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { DealHistoryEntry } from "@/services/dealHistoryApi";
import { parseInstantUtc } from "@/utils/formatDate";

type DealActivityDrawerProps = {
  open: boolean;
  onClose: () => void;
  history: DealHistoryEntry[];
  loading?: boolean;
};

type HistoryGroup = {
  dateChanged: string;
  changedBy: string | null;
  changes: DealHistoryEntry[];
};

function formatActivityDate(dateStr: string): string {
  const date = parseInstantUtc(dateStr);
  if (Number.isNaN(date.getTime())) {
    return dateStr;
  }

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatFieldLabel(fieldName: string): string {
  return fieldName.replace(/_/g, " ");
}

function formatDisplayValue(value: string | null): string {
  if (value == null || value === "" || value === "None") {
    return "—";
  }

  if (value.length > 200) {
    return `${value.slice(0, 200)}…`;
  }

  return value;
}

function groupHistoryEntries(entries: DealHistoryEntry[]): HistoryGroup[] {
  const groups: HistoryGroup[] = [];
  const indexByKey = new Map<string, number>();

  for (const entry of entries) {
    const key = `${entry.date_changed}|${entry.changed_by ?? ""}`;
    const existingIndex = indexByKey.get(key);

    if (existingIndex == null) {
      indexByKey.set(key, groups.length);
      groups.push({
        dateChanged: entry.date_changed,
        changedBy: entry.changed_by,
        changes: [entry],
      });
      continue;
    }

    groups[existingIndex].changes.push(entry);
  }

  return groups;
}

export default function DealActivityDrawer({
  open,
  onClose,
  history,
  loading = false,
}: DealActivityDrawerProps) {
  const groups = useMemo(() => groupHistoryEntries(history), [history]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480 },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography
              sx={{
                fontSize: "1.375rem",
                fontWeight: 700,
                color: "#182737",
                lineHeight: 1.2,
              }}
            >
              Activity
            </Typography>
            <Chip
              label="Deal"
              size="small"
              sx={{
                height: 22,
                fontSize: "0.75rem",
                fontWeight: 500,
                backgroundColor: "#f2f4f7",
                color: "#667085",
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Recent changes to this deal.
          </Typography>
        </Box>
        <IconButton aria-label="Close activity" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          p: 2,
          scrollbarGutter: "stable",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No activity recorded for this deal yet.
          </Typography>
        ) : (
          <Box sx={{ position: "relative" }}>
            {groups.length > 1 && (
              <Box
                sx={{
                  position: "absolute",
                  left: 7.5,
                  top: 10,
                  bottom: 10,
                  width: "1px",
                  backgroundColor: "#e4e7ec",
                }}
              />
            )}
            {groups.map((group, index) => {
              const metaParts = [
                formatActivityDate(group.dateChanged),
                group.changedBy,
              ].filter(Boolean);

              return (
                <Box
                  key={`${group.dateChanged}|${group.changedBy ?? ""}`}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    pb: index < groups.length - 1 ? 2.5 : 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      flexShrink: 0,
                      display: "flex",
                      justifyContent: "center",
                      pt: 0.75,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#98a2b3",
                        position: "relative",
                        zIndex: 1,
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.75 }}
                    >
                      {metaParts.join(" · ")}
                    </Typography>

                    <Chip
                      label="deal.update"
                      size="small"
                      sx={{
                        height: 22,
                        mb: 1,
                        fontSize: "0.6875rem",
                        fontWeight: 600,
                        letterSpacing: "0.02em",
                        backgroundColor: "#eef4ff",
                        color: "#175cd3",
                      }}
                    />

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                      {group.changes.map((change) => (
                        <Typography
                          key={`${change.field_name}-${change.old_value}-${change.new_value}`}
                          variant="body2"
                          sx={{
                            color: "#344054",
                            lineHeight: 1.45,
                            wordBreak: "break-word",
                          }}
                        >
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {formatFieldLabel(change.field_name)}:
                          </Box>{" "}
                          <Box
                            component="span"
                            sx={{ color: "text.secondary", textDecoration: "line-through" }}
                          >
                            {formatDisplayValue(change.old_value)}
                          </Box>{" "}
                          → {formatDisplayValue(change.new_value)}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
